import React from 'react';
import MenuItem from '@material-ui/core/MenuItem';

import './SupplierRequestConfirmationDialog.css';
import DecisionDialog, { DecisionDialogProps } from 'components/dialog/DecisionDialog';
import Select from 'components/input/Select';
import TextField from 'components/input/TextField';

//
// --- Types ---

interface BidTeam {
  bidTeamMemberKeys: ReadonlyArray<number>;
  compositeKey: {
    leadCompanyKey: number;
  };
  name: string;
}

interface BidTeamContentProps {
  bidTeams: ReadonlyArray<BidTeam>;
  leadCompanyKey: number;
  onBidTeamSelectChange: (value: any) => void;
  supplierBidTeam: BidTeam | null;
}

interface Supplier {
  companyName: string;
  email: string;
  name: string;
  workPhone: string;
}

interface SupplierRequestConfirmationDialogProps extends Omit<DecisionDialogProps, 'content' | 'title'> {
  approveSupplierRequest: boolean;
  bidTeams: BidTeam[];
  comments: string;
  onBidTeamSelectChange: (value: number) => void;
  onTextFieldChange: () => void;
  supplier: Supplier | null;
  supplierBidTeam: BidTeam | null;
}

//
// --- Helper Components ---

const BidTeamContent = (props: BidTeamContentProps) => {
  const { bidTeams, leadCompanyKey, onBidTeamSelectChange, supplierBidTeam } = props;
  return (
    <span
      className="supplier-request-confirmation-dialog-dropdown"
      data-testid="supplier-request-confirmation-dialog-dropdown"
    >
      <span>Bid Team: </span>
      {supplierBidTeam ? (
        <span> {supplierBidTeam.name} </span>
      ) : (
        <Select
          className="supplier-request-confirmation-dialog-dropdown-select"
          data-testid="supplier-request-confirmation-dialog-dropdown-select"
          onChange={e => onBidTeamSelectChange(e.target.value)}
          value={leadCompanyKey}
        >
          <MenuItem value={-1}>New Team</MenuItem>
          {bidTeams.map(bidTeam => (
            <MenuItem key={bidTeam.compositeKey.leadCompanyKey} value={bidTeam.compositeKey.leadCompanyKey}>
              {bidTeam.name}
            </MenuItem>
          ))}
        </Select>
      )}
    </span>
  );
};

//
// --- SupplierRequestConfirmationDialog ---

const SupplierRequestConfirmationDialog: React.FC<SupplierRequestConfirmationDialogProps> = props => {
  const {
    approveSupplierRequest,
    bidTeams,
    comments,
    onBidTeamSelectChange,
    onTextFieldChange,
    supplier,
    supplierBidTeam,
    ...remainingDecisionDialogProps
  } = props;

  const [leadCompanyKey, setLeadCompanyKey] = React.useState<number>(-1);

  const handleBidTeamSelectChange = (value: any) => {
    setLeadCompanyKey(value);
    onBidTeamSelectChange(value);
  };

  const requestStatus = approveSupplierRequest ? 'Approved' : 'Denied';

  return (
    <DecisionDialog
      content={
        supplier && (
          <>
            <ul className="supplier-request-confirmation-dialog-bulleted-points">
              <li className="supplier-request-confirmation-dialog-item">
                Clicking "Submit" will {approveSupplierRequest ? 'approve' : 'deny'} <b>{supplier.name}</b> of{' '}
                <b>{supplier.companyName}</b> with a work phone number of <b>{supplier.workPhone}</b> and an email
                address of <b>{supplier.email}</b> access to this bid event
              </li>
              <li className="supplier-request-confirmation-dialog-item">Optional comments can be included below:</li>
            </ul>
            <div>
              <TextField
                className="supplier-request-confirmation-dialog-content-textarea"
                inputProps={{ maxLength: 1000 }}
                label="Comments"
                multiline
                onChange={onTextFieldChange}
                rows={3}
                value={comments}
                variant="outlined"
              />
            </div>
            {approveSupplierRequest && (
              <BidTeamContent
                bidTeams={bidTeams}
                leadCompanyKey={leadCompanyKey}
                onBidTeamSelectChange={handleBidTeamSelectChange}
                supplierBidTeam={supplierBidTeam}
              />
            )}
          </>
        )
      }
      data-testid="supplier-request-confirmation-dialog"
      maxWidth="md"
      title={`Supplier Request ${requestStatus}`}
      {...remainingDecisionDialogProps}
    />
  );
};

export default SupplierRequestConfirmationDialog;
